/**
 * Tiny global pub-sub that lets any component "bump" a refresh signal
 * other components subscribe to. We use it as the foundation for the
 * MyWorld live-update system: every successful mutation (POST / PUT /
 * PATCH / DELETE) in axios automatically bumps the relevant scope, and
 * components include the resulting `version` number in their effect
 * dependency arrays — they refetch without ever needing a full reload.
 *
 * Scopes give us granular invalidation:
 *   • 'all'           — bump after every mutation, all subscribers refetch
 *   • 'kosten'        — bump after orders/services mutations
 *   • 'garage'        — bump after vehicle / tire / fuel mutations
 *   • 'buddies'       — bump after buddy / chat / convoy mutations
 *   • 'feedback'      — bump after feedback mutations
 *
 * Subscribing to 'all' guarantees a refetch on every mutation; finer
 * scopes save bandwidth on heavy pages.
 */
import { useEffect, useState, useSyncExternalStore } from 'react';

const versions = new Map(); // scope -> integer version
const listeners = new Map(); // scope -> Set<callback>

function getVersion(scope) {
  return versions.get(scope) || 0;
}

function notify(scope) {
  versions.set(scope, getVersion(scope) + 1);
  listeners.get(scope)?.forEach((cb) => {
    try { cb(); } catch { /* ignore listener errors */ }
  });
  // The 'all' scope is implicitly bumped by every other scope — anything
  // subscribed to 'all' refetches no matter what changed.
  if (scope !== 'all') {
    versions.set('all', getVersion('all') + 1);
    listeners.get('all')?.forEach((cb) => {
      try { cb(); } catch { /* ignore */ }
    });
  }
}

function subscribe(scope, cb) {
  if (!listeners.has(scope)) listeners.set(scope, new Set());
  listeners.get(scope).add(cb);
  return () => listeners.get(scope)?.delete(cb);
}

/**
 * Bump one or many scopes. Other components currently mounted that
 * subscribed to the same scope (or to 'all') will re-render with a
 * fresh version number.
 */
export function bumpRefresh(...scopes) {
  if (scopes.length === 0) {
    notify('all');
    return;
  }
  scopes.forEach((s) => notify(s));
}

/**
 * Hook — subscribe to one (or more) scopes and receive the current
 * version number. Put the returned `version` into a useEffect dep
 * array to re-fetch whenever something relevant changes.
 *
 *   const refresh = useMyWorldRefresh('garage');
 *   useEffect(() => { load(); }, [load, refresh]);
 */
export function useMyWorldRefresh(scope = 'all') {
  return useSyncExternalStore(
    (cb) => subscribe(scope, cb),
    () => getVersion(scope),
    () => 0,
  );
}

/**
 * Internal helper — guess which scopes to bump based on a successful
 * mutation URL. Centralises the routing rules so call-sites in axios
 * interceptors stay tiny.
 */
export function inferScopesFromUrl(url = '') {
  const u = url.toLowerCase();
  const scopes = new Set(['all']);
  if (u.includes('/api/buddy') || u.includes('/api/convoys') || u.includes('/api/washboxes/meets')) scopes.add('buddies');
  if (u.includes('/api/myworld/tires') || u.includes('/api/myworld/fuel-log') || u.includes('/api/vehicles')) scopes.add('garage');
  if (u.includes('/api/orders') || u.includes('/api/appointments')) scopes.add('kosten');
  if (u.includes('/api/feedback')) scopes.add('feedback');
  if (u.includes('/api/washboxes')) scopes.add('washboxes');
  return [...scopes];
}

